import { useEffect, useState } from 'react';
import { BrainCircuit20Regular, CheckmarkCircle20Regular } from '@fluentui/react-icons';

interface DocumentoItem {
  category?: string | null;
  technicalDocumentStatus?: string | null;
}

interface ExtractionProgressBannerProps {
  documentos: DocumentoItem[];
  startedAt: number | null;
}

const ACTIVE_STATUSES = ['analyzing', 'generating'];
const DONE_STATUSES = ['pending_validation', 'validated', 'failed'];

const formatElapsed = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes === 0) return `${secs}s`;
  return `${minutes}m ${secs.toString().padStart(2, '0')}s`;
};

export default function ExtractionProgressBanner({
  documentos,
  startedAt,
}: ExtractionProgressBannerProps) {
  const [now, setNow] = useState(Date.now());
  const [completedAt, setCompletedAt] = useState<number | null>(null);

  const technicalDocs = documentos.filter(
    (d) => d.category === 'Projeto' || d.category === 'technical',
  );

  const inProgress = technicalDocs.filter((d) =>
    ACTIVE_STATUSES.includes((d.technicalDocumentStatus ?? '').toLowerCase()),
  );
  const pending = technicalDocs.filter(
    (d) => (d.technicalDocumentStatus ?? '').toLowerCase() === 'pending',
  );
  const done = technicalDocs.filter((d) =>
    DONE_STATUSES.includes((d.technicalDocumentStatus ?? '').toLowerCase()),
  );

  const total = inProgress.length + pending.length + done.length;
  const isActive = inProgress.length > 0 || pending.length > 0;
  const allDone = total > 0 && done.length === total;

  // Stop the timer the moment everything is done; freeze elapsed at that point.
  useEffect(() => {
    if (allDone && completedAt === null) {
      setCompletedAt(Date.now());
    }
    if (!allDone && completedAt !== null) {
      setCompletedAt(null);
    }
  }, [allDone, completedAt]);

  useEffect(() => {
    if (startedAt === null || allDone) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [startedAt, allDone]);

  if (!isActive && !allDone) return null;

  const referenceTime = completedAt ?? now;
  const elapsedMs = startedAt !== null ? referenceTime - startedAt : 0;
  const currentDocIndex = done.length + 1;

  let phaseLabel = 'A iniciar extracção...';
  if (inProgress.some((d) => (d.technicalDocumentStatus ?? '').toLowerCase() === 'generating')) {
    phaseLabel = total === 1
      ? 'A gerar linhas BOQ a partir das medições...'
      : `A gerar linhas BOQ (${currentDocIndex}/${total})...`;
  } else if (inProgress.length > 0) {
    phaseLabel = total === 1
      ? 'Claude Vision a analisar a planta — pode demorar 1-3 minutos...'
      : `Claude Vision a analisar planta ${currentDocIndex} de ${total}...`;
  } else if (pending.length > 0 && done.length === 0) {
    phaseLabel = total === 1
      ? 'A enviar para Claude Vision...'
      : `A iniciar extracção de ${total} plantas...`;
  } else if (pending.length > 0) {
    phaseLabel = `Próxima planta na fila (${done.length}/${total} concluídas)...`;
  }

  if (allDone) {
    const failed = technicalDocs.filter(
      (d) => (d.technicalDocumentStatus ?? '').toLowerCase() === 'failed',
    ).length;
    const success = total - failed;
    return (
      <div
        className="flex items-start gap-3 px-4 py-3 w-full"
        style={{ borderRadius: 'var(--radius)', border: '1px solid #86efac', backgroundColor: '#f0fdf4' }}
      >
        <CheckmarkCircle20Regular className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
        <div className="flex-1">
          <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
            Extracção concluída
          </p>
          <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
            {success} {success === 1 ? 'planta processada' : 'plantas processadas'} com sucesso
            {failed > 0 && ` · ${failed} ${failed === 1 ? 'falhou' : 'falharam'}`}
            {startedAt !== null && ` · em ${formatElapsed(elapsedMs)}`}.
            Clica em "Ver Análise" em cada documento para validar e importar as linhas BOQ.
          </p>
        </div>
      </div>
    );
  }

  const progressPercent = total > 0 ? Math.round((done.length / total) * 100) : 0;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 w-full"
      style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe', backgroundColor: '#f0f7ff' }}
    >
      <div className="relative flex-shrink-0 mt-0.5">
        <BrainCircuit20Regular className="w-5 h-5" style={{ color: '#7c3aed' }} />
        <span
          className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
          style={{ backgroundColor: '#7c3aed', animation: 'pulse 1.5s ease-in-out infinite' }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
            {phaseLabel}
          </p>
          {startedAt !== null && (
            <span
              className="text-secondary-text font-mono flex-shrink-0"
              style={{ fontSize: 'var(--text-caption)' }}
              title="Tempo decorrido"
            >
              {formatElapsed(elapsedMs)}
            </span>
          )}
        </div>
        {total > 1 && (
          <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)' }}>
            {done.length} de {total} concluídas · {inProgress.length} a processar · {pending.length} em fila
          </p>
        )}
        <div
          className="mt-2 h-1.5 w-full overflow-hidden relative"
          style={{ borderRadius: '999px', backgroundColor: '#dbeafe' }}
        >
          {total > 1 ? (
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${progressPercent}%`,
                background: 'linear-gradient(90deg, #3b82f6, #7c3aed)',
              }}
            />
          ) : (
            <div
              className="absolute h-full"
              style={{
                width: '40%',
                background: 'linear-gradient(90deg, transparent, #7c3aed, transparent)',
                animation: 'extraction-indeterminate 1.6s ease-in-out infinite',
              }}
            />
          )}
        </div>
        <style>{`
          @keyframes extraction-indeterminate {
            0% { left: -40%; }
            100% { left: 100%; }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
      </div>
    </div>
  );
}
